import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Popup from "reactjs-popup";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./App.css";
import { useAuth } from "./context/AuthContext";

const API_BASE = "https://cur.abduls.at";

// Parses "DD.MM.YYYY" + "HH:MM:SS" into a real Date for sorting/comparisons.
function parseExamDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split(".").map(Number);
  const [h, m, s] = (timeStr || "00:00:00").split(":").map(Number);
  if (!day || !month || !year) return null;
  return new Date(year, month - 1, day, h || 0, m || 0, s || 0);
}

// Strips seconds from any "HH:MM:SS" occurrence, e.g. for display purposes
// ("13:00:00" -> "13:00", "27.09.2026 um 23:55:00" -> "27.09.2026 um 23:55").
function stripSeconds(str) {
  if (!str) return str;
  return str.replace(/(\d{2}:\d{2}):\d{2}/, "$1");
}

// Parses "DD.MM.YYYY um HH:MM:SS" (the registration-deadline format).
function parseGermanDeadline(str) {
  if (!str) return null;
  const [datePart, timePart] = str.split(" um ");
  return parseExamDateTime(datePart, timePart);
}

// "Grundlagen Compilersysteme" (Compilerbau) darf nie gegen Frei/Individuelles
// getauscht werden - dieser Pflichtkurs ist zwingend vorgeschrieben.
const LOCKED_COURSE_PATTERN = /compiler/i;
const isLockedCourse = (course) => LOCKED_COURSE_PATTERN.test(course?.Kurs || "");

function App() {
  const { currentUser, logout, updateFullName } = useAuth();
  const navigate = useNavigate();
  const token = currentUser?.token;
  const displayName = currentUser?.fullName || currentUser?.username || "";

  const [nameDraft, setNameDraft] = useState("");
  const [nameSaving, setNameSaving] = useState(false);

  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState({});
  const [selectedModules, setSelectedModules] = useState({});
  const [selectedPflicht, setSelectedPflicht] = useState(0);
  const [selectedWahl, setSelectedWahl] = useState(0);
  const [courseTypes, setCourseTypes] = useState({});
  const [freeCourses, setFreeCourses] = useState([]);
  // W7: Mathematik - ein Wahlmodul ohne festen Katalog, Kurse werden manuell
  // hinzugefuegt (genau wie bei Freie Wahlfaecher), zaehlt aber zu Wahl ECTS.
  const [mathCourses, setMathCourses] = useState([]);

  // "idle" -> nothing saved yet this session, "saving", "saved", "error"
  const [saveStatus, setSaveStatus] = useState("idle");
  const [hydrated, setHydrated] = useState(false);

  // Prüfungstermine (examinfo.herokuapp.com, via our own PHP proxy)
  const [examOpen, setExamOpen] = useState(false);
  const [examLoading, setExamLoading] = useState(false);
  const [examError, setExamError] = useState("");
  const [exams, setExams] = useState([]);
  const [examsLoaded, setExamsLoaded] = useState(false);
  const [examSearch, setExamSearch] = useState("");

  const ref = useRef(null);
  const closePopup = () => {
    if (ref.current) ref.current.close();
  };
  const kursRef = useRef();
  const typRef = useRef();
  const ectsRef = useRef();
  const semesterRef = useRef();
  const mathKursRef = useRef();
  const mathTypRef = useRef();
  const mathEctsInputRef = useRef();
  const mathSemesterRef = useRef();
  const saveTimerRef = useRef(null);

  const [w1, setW1] = useState(0);
  const [w2, setW2] = useState(0);
  const [w3, setW3] = useState(0);
  const [w4, setW4] = useState(0);
  const [w5, setW5] = useState(0);
  const [w6, setW6] = useState(0);
  const [wahl, setWahl] = useState([]);

  const totalects = 180;
  const totalpflicht = 144;
  const totalwhal = 24;
  const totalfree = 12;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleSaveName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      alert("Name darf nicht leer sein.");
      return;
    }
    setNameSaving(true);
    try {
      const res = await fetch(`${API_BASE}/profile.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ full_name: trimmed }),
      });
      const raw = await res.text();
      let payload;
      try {
        payload = JSON.parse(raw);
      } catch (e) {
        payload = null;
      }
      if (payload?.success) {
        updateFullName(payload.full_name);
        closePopup();
      } else {
        alert(payload?.error || "Name konnte nicht gespeichert werden.");
      }
    } catch (e) {
      console.error("Name speichern fehlgeschlagen:", e);
      alert("Netzwerkfehler beim Speichern des Namens.");
    } finally {
      setNameSaving(false);
    }
  };

  const handleTypeChange = (courseKey, newType) => {
    setCourseTypes((prevTypes) => ({
      ...prevTypes,
      [courseKey]: newType,
    }));
    closePopup();
  };

  // Guards the "Art" change: Frei/Individuelles moves a course's ECTS out of
  // its Modul (into Freie Wahlfaecher resp. Wahlmodul Individuell).
  // Rule (nur fuer Pflichtmodule, per enforceSwapLimit): pro Modul darf
  // maximal ein Kurs getauscht werden. Der Compilerbau-Kurs darf nie
  // getauscht werden, unabhaengig vom Modul.
  const attemptTypeChange = (mod, course, newType, defaultType, enforceSwapLimit) => {
    const courseKey = getCourseKey(course);

    if (newType !== defaultType) {
      if (isLockedCourse(course)) {
        alert(`"${course.Kurs}" ist ein Pflichtkurs, der nicht ersetzt werden kann.`);
        closePopup();
        return;
      }

      if (enforceSwapLimit) {
        const currentType = courseTypes[courseKey] || defaultType;
        const alreadyReassignedItself = currentType !== defaultType;

        if (!alreadyReassignedItself) {
          const otherReassignedInModule = mod.courses.some((c) => {
            const key = getCourseKey(c);
            if (key === courseKey) return false;
            const t = courseTypes[key];
            return t === "Frei" || t === "Individuelles";
          });
          if (otherReassignedInModule) {
            alert(
              `Im Modul "${mod.moduleName}" wurde bereits ein Kurs ersetzt - pro Modul ist nur ein Tausch erlaubt.`
            );
            closePopup();
            return;
          }
        }
      }
    }

    handleTypeChange(courseKey, newType);
  };

  const handleAddCourse = () => {
    const kursValue = kursRef.current.value;
    const ectsValue = ectsRef.current.value;
    if (!kursValue || !ectsValue) {
      alert("Please fill in both 'Kurs' and 'Ects' fields.");
      return;
    }
    if (isNaN(ectsValue) || ectsValue.trim() === "") {
      alert("'Ects' must be a Number");
      return;
    }
    const newCourse = {
      Kurs: kursValue,
      Typ: typRef.current.value || "-",
      ECTS: Number(ectsValue),
      Semester: semesterRef.current.value || "-",
    };

    setFreeCourses((prevCourses) => [...prevCourses, newCourse]);

    kursRef.current.value = "";
    typRef.current.value = "";
    ectsRef.current.value = "";
    semesterRef.current.value = "";
    closePopup();
  };

  const handleDeleteFreeCourse = (index) => {
    setFreeCourses((prev) => prev.filter((_, i) => i !== index));
  };

  // W7: Mathematik - manuelles Hinzufuegen/Entfernen, analog zu Freie Wahlfaecher.
  const handleAddMathCourse = () => {
    const kursValue = mathKursRef.current.value;
    const ectsValue = mathEctsInputRef.current.value;
    if (!kursValue || !ectsValue) {
      alert("Please fill in both 'Kurs' and 'Ects' fields.");
      return;
    }
    if (isNaN(ectsValue) || ectsValue.trim() === "") {
      alert("'Ects' must be a Number");
      return;
    }
    const newCourse = {
      Kurs: kursValue,
      Typ: mathTypRef.current.value || "-",
      ECTS: Number(ectsValue),
      Semester: mathSemesterRef.current.value || "-",
    };

    setMathCourses((prev) => [...prev, newCourse]);

    mathKursRef.current.value = "";
    mathTypRef.current.value = "";
    mathEctsInputRef.current.value = "";
    mathSemesterRef.current.value = "";
    closePopup();
  };

  const handleDeleteMathCourse = (index) => {
    setMathCourses((prev) => prev.filter((_, i) => i !== index));
  };

  const createPositiveValueList = () => {
    const allValues = [
      { label: "w1", value: w1 },
      { label: "w2", value: w2 },
      { label: "w3", value: w3 },
      { label: "w4", value: w4 },
      { label: "w5", value: w5 },
      { label: "w6", value: w6 },
    ];
    setWahl(allValues.filter((item) => item.value > 0));
  };

  const handleModuleCheck = (module, isChecked) => {
    setSelectedModules((prev) => ({ ...prev, [module.moduleName]: isChecked }));
    module.courses.forEach((modd) => {
      handleCheck(modd, isChecked);
    });
  };

  // Courses reassigned to "Frei" or "Individuelles" no longer count towards
  // their own module's ECTS - that ECTS is booked under Freie Wahlfaecher
  // resp. Wahlmodul Individuell instead (see reassignedFree/reassignedIndividuell below).
  const calculateModuleECTS = (module) => {
    return module.courses.reduce((sum, course) => {
      const courseKey = getCourseKey(course);
      if (!selectedCourses.hasOwnProperty(courseKey)) return sum;
      const type = courseTypes[courseKey];
      // Egal ob Pflicht- oder Wahlmodul: sobald ein Kurs auf "Frei" oder
      // "Individuelles" umgestellt ist, zaehlt er nicht mehr zu seinem
      // urspruenglichen Modul (siehe reassignedFree/reassignedIndividuell unten).
      if (type === "Frei" || type === "Individuelles") return sum;
      return sum + Number(selectedCourses[courseKey]);
    }, 0);
  };

  const areAllModuleCoursesSelected = (module) => {
    return module.courses.every((modd) =>
      selectedCourses.hasOwnProperty(getCourseKey(modd))
    );
  };

  useEffect(() => {
    createPositiveValueList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w1, w2, w3, w4, w5, w6]);

  const sumOfModule = (mod) => {
    switch (mod.mn) {
      case "W1":
        setW1(calculateModuleECTS(mod));
        break;
      case "W2":
        setW2(calculateModuleECTS(mod));
        break;
      case "W3":
        setW3(calculateModuleECTS(mod));
        break;
      case "W4":
        setW4(calculateModuleECTS(mod));
        break;
      case "W5":
        setW5(calculateModuleECTS(mod));
        break;
      case "W6":
        setW6(calculateModuleECTS(mod));
        break;
      default:
        break;
    }
  };

  function updateSumOfPflichtECTS() {
    const total = courses.slice(0, 10).reduce((acc, mod) => {
      return acc + calculateModuleECTS(mod);
    }, 0);
    setSelectedPflicht(total);
  }

  function updateSumOfWahlECTS() {
    const total = courses.slice(10, 17).reduce((acc, mod) => {
      sumOfModule(mod);
      return acc + calculateModuleECTS(mod);
    }, 0);
    // "Individuelles" umgestellte Pflichtkurse (W8) und W7-Mathematik-Kurse
    // zaehlen zusaetzlich zu den Wahl ECTS dazu.
    setSelectedWahl(total + reassignedIndividuellEcts + mathEcts);
  }

  const getCourseKey = (course) => `${course.Kurs}-${course.Typ}`;

  const handleCheck = (course, isChecked) => {
    setSelectedCourses((prevSelected) => {
      const newSelected = { ...prevSelected };
      const courseKey = getCourseKey(course);
      if (isChecked) {
        newSelected[courseKey] = course.ECTS;
      } else {
        delete newSelected[courseKey];
      }
      return newSelected;
    });
  };

  // Kurse (Pflicht- UND Wahlmodule), deren Art auf "Frei" bzw.
  // "Individuelles" umgestellt wurde - werden unten bei Freie Wahlfaecher /
  // Wahlmodul Individuell gelistet und dort mitgezaehlt statt bei ihrem
  // urspruenglichen Modul.
  const reassignedFreeCourses = [];
  const reassignedIndividuellCourses = [];
  courses.slice(0, 17).forEach((mod) => {
    mod.courses.forEach((course) => {
      const key = getCourseKey(course);
      if (!selectedCourses.hasOwnProperty(key)) return;
      const type = courseTypes[key];
      if (type === "Frei") {
        reassignedFreeCourses.push({ ...course, _moduleName: mod.moduleName, _key: key });
      } else if (type === "Individuelles") {
        reassignedIndividuellCourses.push({ ...course, _moduleName: mod.moduleName, _key: key });
      }
    });
  });
  const reassignedFreeEcts = reassignedFreeCourses.reduce(
    (s, c) => s + Number(c.ECTS || 0),
    0
  );
  const reassignedIndividuellEcts = reassignedIndividuellCourses.reduce(
    (s, c) => s + Number(c.ECTS || 0),
    0
  );

  // Manuell hinzugefuegte freie Wahlfaecher sind NICHT Teil von selectedCourses
  // (sie kommen aus einer eigenen Liste), umgestellte Pflichtkurse dagegen
  // schon (sie bleiben "checked") - daher hier sauber trennen, damit nichts
  // doppelt gezaehlt wird.
  const manualFreeEcts = freeCourses.reduce((sum, c) => sum + Number(c.ECTS || 0), 0);
  const sumOfFree = manualFreeEcts + reassignedFreeEcts;

  // W7: Mathematik - ebenfalls eine eigene, manuell gepflegte Liste (wie freeCourses).
  const mathEcts = mathCourses.reduce((sum, c) => sum + Number(c.ECTS || 0), 0);

  // W1-W6 kommen aus dem Katalog (siehe sumOfModule), W7 (Mathematik) und W8
  // (Individuell) werden hier ergaenzt, damit sie oben genauso als Kachel
  // erscheinen wie die anderen Wahlmodule.
  const wxTiles = [
    ...wahl,
    ...(mathEcts > 0 ? [{ label: "W7", value: mathEcts }] : []),
    ...(reassignedIndividuellEcts > 0 ? [{ label: "W8", value: reassignedIndividuellEcts }] : []),
  ];

  const sumOfECTS =
    Object.values(selectedCourses).reduce((sum, ects) => sum + Number(ects), 0) +
    manualFreeEcts +
    mathEcts;

  useEffect(() => {
    updateSumOfPflichtECTS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, selectedCourses, courseTypes]);

  useEffect(() => {
    updateSumOfWahlECTS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, selectedCourses, courseTypes, mathCourses]);

  // Load the course catalog (static file).
  useEffect(() => {
    async function fetchData() {
      const res = await fetch("data2.json");
      const data = await res.json();
      setCourses(data.modules);
      setLoading(false);
    }
    fetchData();
  }, []);

  // Load this user's previously saved selections from the backend.
  useEffect(() => {
    if (!token) return;
    async function loadSavedSelections() {
      try {
        const res = await fetch(`${API_BASE}/courses.php`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const raw = await res.text();
        let payload;
        try {
          payload = JSON.parse(raw);
        } catch (e) {
          console.error("courses.php lieferte kein gueltiges JSON:", raw);
          payload = null;
        }
        if (payload?.success && payload.data) {
          setSelectedCourses(payload.data.selectedCourses || {});
          setFreeCourses(payload.data.freeCourses || []);
          setCourseTypes(payload.data.courseTypes || {});
          setMathCourses(payload.data.mathCourses || []);
        }
      } catch (e) {
        console.error("Konnte gespeicherte Kurse nicht laden:", e);
      } finally {
        setHydrated(true);
      }
    }
    loadSavedSelections();
  }, [token]);

  // Auto-save: debounce writes so we don't hammer the server on every click.
  useEffect(() => {
    if (!hydrated || !token) return;
    setSaveStatus("saving");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/courses.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ selectedCourses, freeCourses, courseTypes, mathCourses }),
        });
        const raw = await res.text();
        let payload;
        try {
          payload = JSON.parse(raw);
        } catch (e) {
          payload = null;
        }
        setSaveStatus(payload?.success ? "saved" : "error");
      } catch (e) {
        console.error("Speichern fehlgeschlagen:", e);
        setSaveStatus("error");
      }
    }, 800);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourses, freeCourses, courseTypes, mathCourses, hydrated, token]);

  const saveStatusLabel =
    {
      idle: "",
      saving: "Speichert…",
      saved: "Gespeichert",
      error: "Fehler beim Speichern",
    }[saveStatus] || "";

  // ---------- PDF-Export ----------
  const handleExportPdf = () => {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString("de-DE");

    doc.setFontSize(16);
    doc.text("ECTS Tracker - Report", 14, 18);
    doc.setFontSize(10);
    doc.text(`Name: ${displayName || "-"}`, 14, 26);
    doc.text(`Datum: ${today}`, 14, 32);

    doc.setFontSize(11);
    doc.text(
      `Total: ${sumOfECTS} / ${totalects} ECTS   |   Pflicht: ${selectedPflicht} / ${totalpflicht}   |   Wahl: ${selectedWahl} / ${totalwhal}   |   Frei: ${sumOfFree} / ${totalfree}`,
      14,
      42
    );

    const rows = [];

    courses.slice(0, 10).forEach((mod) => {
      mod.courses.forEach((c) => {
        const key = getCourseKey(c);
        if (!selectedCourses.hasOwnProperty(key)) return;
        const type = courseTypes[key] || "pflicht";
        rows.push([mod.moduleName, c.Kurs, c.Typ, c.ECTS, type]);
      });
    });

    courses.slice(10, 17).forEach((mod) => {
      mod.courses.forEach((c) => {
        const key = getCourseKey(c);
        if (!selectedCourses.hasOwnProperty(key)) return;
        const type = courseTypes[key] || "wahlmodul";
        rows.push([mod.moduleName, c.Kurs, c.Typ, c.ECTS, type]);
      });
    });

    mathCourses.forEach((c) => {
      rows.push(["W7: Mathematik", c.Kurs, c.Typ, c.ECTS, "wahl"]);
    });

    freeCourses.forEach((c) => {
      rows.push(["Freie Wahlfächer", c.Kurs, c.Typ, c.ECTS, "frei"]);
    });

    doc.autoTable({
      startY: 50,
      head: [["Modul", "Kurs", "Typ", "ECTS", "Art"]],
      body: rows,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [79, 157, 255] },
      didDrawPage: () => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(
          `Seite ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`,
          14,
          doc.internal.pageSize.getHeight() - 8
        );
      },
    });

    const fileSafeName = (displayName || "ects-tracker")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    doc.save(`${fileSafeName || "ects-tracker"}-report.pdf`);
  };

  // ---------- Prüfungstermine (examinfo.herokuapp.com via proxy) ----------
  const loadExams = useCallback(async () => {
    setExamLoading(true);
    setExamError("");
    try {
      const res = await fetch(`${API_BASE}/examinfo_proxy.php`);
      const raw = await res.text();
      let payload;
      try {
        payload = JSON.parse(raw);
      } catch (e) {
        throw new Error("Ungültige Antwort vom Server");
      }
      if (!payload?.success) {
        throw new Error(payload?.error || "Unbekannter Fehler");
      }
      setExams(Array.isArray(payload.exams) ? payload.exams : []);
      setExamsLoaded(true);
    } catch (e) {
      console.error("Konnte Prüfungstermine nicht laden:", e);
      setExamError("Prüfungstermine konnten nicht geladen werden.");
    } finally {
      setExamLoading(false);
    }
  }, []);

  const handleOpenExams = () => {
    setExamOpen(true);
    if (!examsLoaded) {
      loadExams();
    }
  };

  useEffect(() => {
    if (!examOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setExamOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [examOpen]);

  const sortedExams = useMemo(() => {
    const now = new Date();
    return [...exams]
      .map((exam) => ({
        ...exam,
        _dateObj: parseExamDateTime(exam.date, exam.start),
        _deadline: parseGermanDeadline(exam.endreg),
      }))
      .filter((exam) =>
        exam.titel?.toLowerCase().includes(examSearch.trim().toLowerCase())
      )
      .sort((a, b) => (a._dateObj?.getTime() || 0) - (b._dateObj?.getTime() || 0))
      .map((exam) => ({
        ...exam,
        _isUpcoming: exam._dateObj ? exam._dateObj >= now : true,
        _registrationOpen: exam._deadline ? exam._deadline >= now : false,
      }));
  }, [exams, examSearch]);

  const renderBar = (value, total) => {
    const pct = total > 0 ? Math.min(100, Math.max(0, (value / total) * 100)) : 0;
    return (
      <div className="bar-track">
        <div className="bar-fill" style={{ width: `${pct}%` }} />
      </div>
    );
  };

  const renderModuleCard = (
    mod,
    index,
    totalKey,
    showTypeBadge,
    cardOffset,
    defaultType,
    enforceSwapLimit
  ) => {
    const isModuleSelected =
      selectedModules[mod.moduleName] || areAllModuleCoursesSelected(mod);
    const moduleECTS = calculateModuleECTS(mod);
    const moduleTotal = mod[totalKey] || 0;

    return (
      <div
        className="module-card"
        key={`module-${totalKey}-${index}`}
        style={{ "--card-i": cardOffset + index }}
      >
        <div className="module-card-header">
          <div className="module-card-name">{mod.moduleName}</div>
          <div className="module-card-progress">
            {renderBar(moduleECTS, moduleTotal)}
            <span className="module-card-ects">
              {moduleECTS} / {moduleTotal} ECTS
            </span>
          </div>
          <input
            type="checkbox"
            className="module-select-all"
            checked={isModuleSelected}
            onChange={(e) => handleModuleCheck(mod, e.target.checked)}
            title="Alle Kurse dieses Moduls auswaehlen"
          />
        </div>
        <div className="course-table-wrap">
          <table className="course-table">
            <thead>
              <tr>
                <th>Kurs</th>
                <th>Typ</th>
                <th>ECTS</th>
                <th>Semester</th>
                {showTypeBadge && <th>Art</th>}
                <th>Erledigt</th>
              </tr>
            </thead>
            <tbody>
              {mod.courses.map((modd) => {
                const courseKey = getCourseKey(modd);
                const isChecked = selectedCourses.hasOwnProperty(courseKey);
                const courseType = courseTypes[courseKey] || defaultType;

                return (
                  <tr key={courseKey} className={isChecked ? "is-selected" : undefined}>
                    <td>{modd.Kurs}</td>
                    <td>{modd.Typ}</td>
                    <td>{modd.ECTS}</td>
                    <td>{modd.Semester}</td>
                    {showTypeBadge && (
                      <td>
                        <Popup
                          ref={ref}
                          trigger={
                            <button type="button" className="type-badge">
                              {courseType}
                            </button>
                          }
                          modal
                        >
                          <div>
                            <div className="popup-title">Art festlegen</div>
                            {isLockedCourse(modd) && (
                              <p className="popup-hint">
                                Dieser Pflichtkurs kann nicht ersetzt werden.
                              </p>
                            )}
                            <div className="type-popup-options">
                              <button
                                className="btn-primary"
                                onClick={() =>
                                  attemptTypeChange(
                                    mod,
                                    modd,
                                    defaultType,
                                    defaultType,
                                    enforceSwapLimit
                                  )
                                }
                              >
                                {defaultType}
                              </button>
                              <button
                                className="btn-primary"
                                onClick={() =>
                                  attemptTypeChange(mod, modd, "Frei", defaultType, enforceSwapLimit)
                                }
                              >
                                Frei
                              </button>
                              <button
                                className="btn-primary"
                                onClick={() =>
                                  attemptTypeChange(
                                    mod,
                                    modd,
                                    "Individuelles",
                                    defaultType,
                                    enforceSwapLimit
                                  )
                                }
                              >
                                Individuelles
                              </button>
                            </div>
                          </div>
                        </Popup>
                      </td>
                    )}
                    <td>
                      <input
                        type="checkbox"
                        className="course-checkbox"
                        checked={isChecked}
                        onChange={(e) => handleCheck(modd, e.target.checked)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <header className="topbar">
        <div className="topbar-title">ECTS Tracker</div>
        <div className="topbar-right">
          {saveStatusLabel && (
            <span className={`save-status save-status--${saveStatus}`}>
              {saveStatusLabel}
            </span>
          )}
          <button className="btn-ghost btn-exams" onClick={handleOpenExams}>
            🎓 Prüfungen
          </button>
          <button className="btn-ghost" onClick={handleExportPdf}>
            📄 PDF Export
          </button>
          {currentUser && (
            <Popup
              ref={ref}
              trigger={
                <button
                  type="button"
                  className="btn-ghost topbar-name"
                  onClick={() => setNameDraft(displayName)}
                >
                  {displayName} ✎
                </button>
              }
              modal
            >
              <div>
                <div className="popup-title">Name festlegen</div>
                <input
                  type="text"
                  placeholder="Dein Name"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                />
                <button
                  className="btn-primary"
                  onClick={handleSaveName}
                  disabled={nameSaving}
                >
                  {nameSaving ? "Speichert…" : "Speichern"}
                </button>
              </div>
            </Popup>
          )}
          <button className="btn-ghost" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="app-shell app-fade-in">
        {loading ? (
          <div className="loading-state">Lade Kursdaten…</div>
        ) : (
          <>
            <section className="summary-grid">
              <div className="stat-card" style={{ "--card-i": 0 }}>
                <div className="stat-card-label">Total ECTS</div>
                <div className="stat-card-value">
                  {sumOfECTS} <span>/ {totalects}</span>
                </div>
                {renderBar(sumOfECTS, totalects)}
              </div>
              <div className="stat-card" style={{ "--card-i": 1 }}>
                <div className="stat-card-label">Pflicht ECTS</div>
                <div className="stat-card-value">
                  {selectedPflicht} <span>/ {totalpflicht}</span>
                </div>
                {renderBar(selectedPflicht, totalpflicht)}
              </div>
              <div className="stat-card" style={{ "--card-i": 2 }}>
                <div className="stat-card-label">Wahl ECTS</div>
                <div className="stat-card-value">
                  {selectedWahl} <span>/ {totalwhal}</span>
                </div>
                {renderBar(selectedWahl, totalwhal)}
              </div>
              <div className="stat-card" style={{ "--card-i": 3 }}>
                <div className="stat-card-label">Freie Wahlfächer</div>
                <div className="stat-card-value">
                  {sumOfFree} <span>/ {totalfree}</span>
                </div>
                {renderBar(sumOfFree, totalfree)}
              </div>
            </section>

            {wxTiles.length > 0 && (
              <section className="wx-grid">
                {wxTiles.map((w, idx) => (
                  <div className="wx-tile" key={w.label || idx}>
                    <div className="wx-label">{w.label}</div>
                    <div className="mini-bar">
                      <div
                        className="mini-bar-fill"
                        style={{ width: `${Math.min(100, (w.value / 8) * 100)}%` }}
                      />
                    </div>
                    <div className="wx-value">{w.value} / 8 ECTS</div>
                  </div>
                ))}
              </section>
            )}

            <h2 className="section-title">Pflicht</h2>
            <div className="module-grid">
              {courses.slice(0, 10).map((mod, index) =>
                renderModuleCard(mod, index, "total", true, 0, "pflicht", true)
              )}
            </div>

            <h2 className="section-title">Wahl</h2>
            <div className="module-grid">
              {courses.slice(10, 17).map((mod, index) =>
                renderModuleCard(mod, index, "should", true, 10, "wahlmodul", false)
              )}

              <div className="module-card" style={{ "--card-i": 16 }}>
                <div className="module-card-header">
                  <div className="module-card-name">W7: Mathematik</div>
                  <div className="module-card-progress">
                    {renderBar(mathEcts, 8)}
                    <span className="module-card-ects">{mathEcts} / 8 ECTS</span>
                  </div>
                </div>
                <div className="course-table-wrap">
                  <table className="course-table">
                    <thead>
                      <tr>
                        <th>Kurs</th>
                        <th>Typ</th>
                        <th>ECTS</th>
                        <th>Semester</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {mathCourses.length === 0 && (
                        <tr className="empty-row">
                          <td colSpan={5}>Noch keine Mathematik-Kurse hinzugefügt.</td>
                        </tr>
                      )}
                      {mathCourses.map((modd, idx) => (
                        <tr key={`${getCourseKey(modd)}-math-${idx}`}>
                          <td>{modd.Kurs}</td>
                          <td>{modd.Typ}</td>
                          <td>{modd.ECTS}</td>
                          <td>{modd.Semester}</td>
                          <td>
                            <button
                              className="btn-danger-ghost"
                              onClick={() => handleDeleteMathCourse(idx)}
                            >
                              Löschen
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="add-course-row">
                  <Popup
                    ref={ref}
                    trigger={
                      <button type="button" className="btn-primary">
                        + Kurs hinzufügen
                      </button>
                    }
                    modal
                  >
                    <div>
                      <div className="popup-title">Neuer Mathematik-Kurs</div>
                      <input type="text" placeholder="Kurs" ref={mathKursRef} />
                      <input type="text" placeholder="Typ" ref={mathTypRef} />
                      <input type="text" placeholder="Ects" ref={mathEctsInputRef} />
                      <input type="text" placeholder="Semester" ref={mathSemesterRef} />
                      <button className="btn-primary" onClick={handleAddMathCourse}>
                        Hinzufügen
                      </button>
                    </div>
                  </Popup>
                </div>
              </div>
            </div>

            {reassignedIndividuellCourses.length > 0 && (
              <>
                <h2 className="section-title">Wahlmodul Individuell</h2>
                <div className="module-card" style={{ "--card-i": 17 }}>
                  <div className="module-card-header">
                    <div className="module-card-name">Individuell umgestellte Pflichtkurse</div>
                    <div className="module-card-ects">{reassignedIndividuellEcts} ECTS</div>
                  </div>
                  <div className="course-table-wrap">
                    <table className="course-table">
                      <thead>
                        <tr>
                          <th>Kurs</th>
                          <th>Typ</th>
                          <th>ECTS</th>
                          <th>Modul</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {reassignedIndividuellCourses.map((modd) => (
                          <tr key={modd._key}>
                            <td>{modd.Kurs}</td>
                            <td>{modd.Typ}</td>
                            <td>{modd.ECTS}</td>
                            <td>{modd._moduleName}</td>
                            <td>
                              <button
                                className="btn-danger-ghost"
                                onClick={() => handleTypeChange(modd._key, "pflicht")}
                              >
                                Zurücksetzen
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            <h2 className="section-title">Freie Wahlfächer</h2>
            <div className="module-card" style={{ "--card-i": 18 }}>
              <div className="course-table-wrap">
                <table className="course-table">
                  <thead>
                    <tr>
                      <th>Kurs</th>
                      <th>Typ</th>
                      <th>ECTS</th>
                      <th>Semester / Modul</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {freeCourses.length === 0 && reassignedFreeCourses.length === 0 && (
                      <tr className="empty-row">
                        <td colSpan={5}>Noch keine freien Wahlfächer hinzugefügt.</td>
                      </tr>
                    )}
                    {reassignedFreeCourses.map((modd) => (
                      <tr key={modd._key}>
                        <td>{modd.Kurs}</td>
                        <td>{modd.Typ}</td>
                        <td>{modd.ECTS}</td>
                        <td>aus {modd._moduleName}</td>
                        <td>
                          <button
                            className="btn-danger-ghost"
                            onClick={() => handleTypeChange(modd._key, "pflicht")}
                          >
                            Zurücksetzen
                          </button>
                        </td>
                      </tr>
                    ))}
                    {freeCourses.map((modd, idx) => (
                      <tr key={`${getCourseKey(modd)}-${idx}`}>
                        <td>{modd.Kurs}</td>
                        <td>{modd.Typ}</td>
                        <td>{modd.ECTS}</td>
                        <td>{modd.Semester}</td>
                        <td>
                          <button
                            className="btn-danger-ghost"
                            onClick={() => handleDeleteFreeCourse(idx)}
                          >
                            Löschen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="add-course-row">
                <Popup
                  ref={ref}
                  trigger={
                    <button type="button" className="btn-primary">
                      + Wahlfach hinzufügen
                    </button>
                  }
                  modal
                >
                  <div>
                    <div className="popup-title">Neues Wahlfach</div>
                    <input type="text" placeholder="Kurs" ref={kursRef} />
                    <input type="text" placeholder="Typ" ref={typRef} />
                    <input type="text" placeholder="Ects" ref={ectsRef} />
                    <input type="text" placeholder="Semester" ref={semesterRef} />
                    <button className="btn-primary" onClick={handleAddCourse}>
                      Hinzufügen
                    </button>
                  </div>
                </Popup>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ---------- Prüfungstermine drawer ---------- */}
      <div
        className={`exam-backdrop${examOpen ? " is-open" : ""}`}
        onClick={() => setExamOpen(false)}
      />
      <aside className={`exam-drawer${examOpen ? " is-open" : ""}`} aria-hidden={!examOpen}>
        <div className="exam-drawer-header">
          <div>
            <div className="exam-drawer-title">Aktuelle Prüfungen</div>
            <div className="exam-drawer-subtitle">Live von examinfo.herokuapp.com</div>
          </div>
          <button className="exam-drawer-close" onClick={() => setExamOpen(false)}>
            ✕
          </button>
        </div>

        <div className="exam-drawer-search">
          <input
            type="text"
            placeholder="Kurs suchen…"
            value={examSearch}
            onChange={(e) => setExamSearch(e.target.value)}
          />
        </div>

        <div className="exam-drawer-list">
          {examLoading && <div className="exam-drawer-state">Lade Prüfungstermine…</div>}

          {!examLoading && examError && (
            <div className="exam-drawer-state exam-drawer-state--error">
              {examError}
              <div style={{ marginTop: "0.6rem" }}>
                <button className="btn-ghost" onClick={loadExams}>
                  Erneut versuchen
                </button>
              </div>
            </div>
          )}

          {!examLoading && !examError && sortedExams.length === 0 && (
            <div className="exam-drawer-state">Keine Prüfungen gefunden.</div>
          )}

          {!examLoading &&
            !examError &&
            sortedExams.map((exam, idx) => (
              <div
                className="exam-item"
                key={`${exam.titel}-${exam.date}-${exam.start}-${idx}`}
                style={{ "--card-i": idx, opacity: exam._isUpcoming ? 1 : 0.55 }}
              >
                <div className="exam-item-top">
                  <div className="exam-item-title">{exam.titel}</div>
                  <div className="exam-item-date">{exam.date}</div>
                </div>
                <div className="exam-item-meta">
                  <span>
                    {stripSeconds(exam.start)} – {stripSeconds(exam.end)}
                  </span>
                  <span>{exam.room}</span>
                  <span>{exam.sem}</span>
                </div>
                <span
                  className={`exam-reg-pill ${
                    exam._registrationOpen ? "exam-reg-pill--open" : "exam-reg-pill--closed"
                  }`}
                >
                  {exam._registrationOpen
                    ? `Anmeldung bis ${stripSeconds(exam.endreg)}`
                    : "Anmeldung geschlossen"}
                </span>
              </div>
            ))}
        </div>
      </aside>
    </div>
  );
}

export default App;
